import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, TrendingUp, Newspaper, Award, MonitorPlay, UserCheck, ArrowRight } from 'lucide-react';
import { fadeUp, staggerParent, scaleIn, viewportOnce } from './motionPresets';

const TRAINING_IMG =
  'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80';

const ITEMS = [
  { icon: BookOpen, label: 'Product Education' },
  { icon: TrendingUp, label: 'Sales Training' },
  { icon: Newspaper, label: 'Industry Updates' },
  { icon: Award, label: 'Certifications' },
  { icon: MonitorPlay, label: 'Live & Virtual Training' },
  { icon: UserCheck, label: 'Agent Development' },
];

export default function TrainingDevelopment() {
  return (
    <section id="training" className="scroll-mt-28 lg:scroll-mt-32 bg-[#f8fafc] py-20 sm:py-28 lg:py-32" data-testid="training-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <motion.div variants={scaleIn} initial="hidden" whileInView="show" viewport={viewportOnce} className="lg:col-span-6 order-2 lg:order-1">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <img src={TRAINING_IMG} alt="KR agent training session" loading="lazy" className="w-full h-[22rem] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#071533]/70 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-white/95 backdrop-blur px-4 py-3 flex items-center gap-3">
              <span className="w-10 h-10 rounded-lg bg-[#0f2a5e] text-[#f5c876] flex items-center justify-center"><Award size={18} /></span>
              <div>
                <div className="text-sm font-bold text-slate-900">KR Academy</div>
                <div className="text-xs text-slate-500">Live & on-demand education year-round</div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div variants={staggerParent} initial="hidden" whileInView="show" viewport={viewportOnce} className="lg:col-span-6 order-1 lg:order-2">
          <motion.span variants={fadeUp} className="text-xs font-semibold uppercase tracking-widest text-[#b8862d]">Invest in your success</motion.span>
          <motion.h2 variants={fadeUp} className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Training &amp; development.</motion.h2>
          <motion.p variants={fadeUp} className="mt-5 text-lg text-slate-600 leading-relaxed">
            Gain the knowledge, skills, and confidence to grow. Access product education, sales training, industry
            updates, certifications, and mentorship built into the KR culture.
          </motion.p>

          <motion.ul variants={staggerParent} className="mt-8 grid sm:grid-cols-2 gap-3">
            {ITEMS.map((it) => (
              <motion.li key={it.label} variants={fadeUp} className="flex items-center gap-3 rounded-xl bg-white border border-slate-200 px-4 py-3 hover:border-[#b8862d]/50 hover:shadow-md transition-all">
                <span className="w-9 h-9 rounded-lg bg-[#0f2a5e]/5 text-[#0f2a5e] flex items-center justify-center shrink-0"><it.icon size={17} /></span>
                <span className="text-sm font-medium text-slate-800">{it.label}</span>
              </motion.li>
            ))}
          </motion.ul>

          <motion.div variants={fadeUp} className="mt-8">
            <Link to="/become-an-agent" data-testid="training-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0f2a5e] text-white font-semibold hover:bg-[#0a1f47] hover:-translate-y-0.5 transition-all">
              Explore training &amp; resources <ArrowRight size={16} />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
